/**
 * Auf welcher Seite die **Werkzeug-Pille** steht — links oder rechts (siehe
 * CONTEXT.md, Werkzeug-Pille).
 *
 * **Gerätelokal und für alle Projekte.** Wer die Pille rechts will, will das
 * überall, wie ein Linkshänder die Maus — deshalb EIN Eintrag und kein Feld je
 * Projekt, und kein Sync: Händigkeit hängt an Hand und Gerät, nicht am Inhalt.
 *
 * **Alle offenen Canvases folgen sofort.** Die Seite lebt deshalb in diesem
 * Modul-Store mit Zuhörern (dasselbe Muster wie `subscribeCanvasFontReady` in
 * `canvasNoteStyle.ts`); `localStorage` ist nur die Ablage über Neustarts
 * hinweg. Ein `storage`-Ereignis täte es nicht: Die Tabs der App leben im
 * selben Dokument, und dort feuert es nicht.
 *
 * Bewusst ohne React — die React-Hälfte steht in `hooks/useCanvasToolbarSide.ts`.
 */

export type CanvasToolbarSide = 'left' | 'right'

export const CANVAS_TOOLBAR_SIDE_KEY = 'happytask:canvas:toolbarSide'

const DEFAULT_SIDE: CanvasToolbarSide = 'left'

const isSide = (value: unknown): value is CanvasToolbarSide =>
  value === 'left' || value === 'right'

/** `undefined` = noch nicht aus dem Speicher geholt. */
let side: CanvasToolbarSide | undefined
const listeners = new Set<() => void>()

function readStored(): CanvasToolbarSide {
  try {
    const raw = localStorage.getItem(CANVAS_TOOLBAR_SIDE_KEY)
    return isSide(raw) ? raw : DEFAULT_SIDE
  } catch {
    // Im privaten Fenster wirft schon das Lesen.
    return DEFAULT_SIDE
  }
}

/** Die aktuelle Seite. Beim ersten Aufruf aus dem Speicher geholt. */
export function getCanvasToolbarSide(): CanvasToolbarSide {
  if (side === undefined) side = readStored()
  return side
}

/**
 * Setzt die Seite, merkt sie und sagt allen Zuhörern Bescheid — nur bei einer
 * echten Änderung, sonst renderte jedes Panel umsonst.
 *
 * Gilt auch, wenn der Speicher wegbricht: Die Seite ist Bequemlichkeit, kein
 * Inhalt, und im laufenden Programm soll sie trotzdem stimmen.
 */
export function setCanvasToolbarSide(next: CanvasToolbarSide): void {
  if (getCanvasToolbarSide() === next) return
  side = next
  try {
    localStorage.setItem(CANVAS_TOOLBAR_SIDE_KEY, next)
  } catch {
    // Kein Speicher (privates Fenster, volles Kontingent).
  }
  for (const listener of listeners) listener()
}

/** Meldet einen Zuhörer für den Seitenwechsel an. Gibt das Abmelden zurück. */
export function subscribeCanvasToolbarSide(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Vergisst Seite und Zuhörer — nur für Tests, die den Modulzustand teilen. */
export function resetCanvasToolbarSideForTests(): void {
  side = undefined
  listeners.clear()
}
